import React, { Fragment, useState, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import { showAlertFailure, showAlertSuccess } from 'client/methods'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import AuthFormFooter from 'components/auth/AuthFormFooter'
import AuthTitle from 'components/internal/AuthTitle'
import Button from 'components/buttons/Button'
import Card from 'components/cards/Card'
import Column from 'components/base/Column'
import dx from 'lib/dashx'
import Row from 'components/base/Row'
import Stack from 'components/base/Stack'
import Text from 'components/typography/Text'
import TextInput from 'components/inputs/TextInput'
import useClientMutation from 'hooks/useClientMutation'
import useMutation from 'hooks/useMutation'
import { ADD_ACCOUNT_TOKEN, LOGIN } from 'client/state/auth'
import {
  COMPLETE_MFA_ENROLLMENT_MUTATION,
  SEND_MFA_CODE_MUTATION,
  VERIFY_MFA_CODE_MUTATION
} from 'models/MFA'

const useStyles = createUseStyles({
  pointer: {
    cursor: 'pointer'
  },
  resend: {
    marginTop: 60
  }
})

function MfaVerifyPage() {
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()
  const classes = useStyles()
  const inputRef = useRef(null)

  const [addAccountToken] = useClientMutation(ADD_ACCOUNT_TOKEN)
  const [login] = useClientMutation(LOGIN)

  const account = state?.account
  const session = state?.session
  const mfaContext = state?.mfaContext
  const channel = state?.channel || 'totp'
  const isEnrollmentBackup = state?.isEnrollmentBackup

  const [code, setCode] = useState('')

  const backupMasked =
    mfaContext?.backup_target_masked?.targetMasked ||
    mfaContext?.backup_target_masked

  const backupChannel = mfaContext?.mfaBackupChannel

  const isAuthenticator = channel === 'totp'
  const isSms = channel === 'sms'
  const isEmail = channel === 'email'

  const title = t(`mfa.verify.title.${channel}`)

  const subtitle =
    (isAuthenticator && t('mfa.verify.subtitle.totp')) ||
    (isSms &&
      t('mfa.verify.subtitle.sms', {
        target: backupMasked || ''
      })) ||
    (isEmail &&
      t('mfa.verify.subtitle.email', {
        target: account?.email || ''
      })) ||
    ''

  const [completeMfaEnrollment] = useMutation(
    COMPLETE_MFA_ENROLLMENT_MUTATION,
    {
      onCompleted: ({ completeMfaEnrollment: newSession }) => {
        const verifiedSession = newSession
        const { accessToken, dashxToken } = verifiedSession
        const accountId = account.id

        addAccountToken({ accountId, dashxToken, token: accessToken })
        dx.setIdentity(accountId, dashxToken)

        login({ accountId })
      },
      onError: () => {
        showAlertFailure({
          message: t('mfa.verify.invalidCode')
        })
        setCode('')
        setTimeout(() => inputRef.current?.focus(), 0)
      }
    }
  )

  const [verifyMfaCode] = useMutation(VERIFY_MFA_CODE_MUTATION, {
    onCompleted: ({ verifyMfaCode: newSession }) => {
      const verifiedSession = newSession
      const { accessToken, dashxToken } = verifiedSession
      const accountId = account.id

      if (isEnrollmentBackup) {
        history.push({
          pathname: '/mfa/setup-success',
          state: {
            account,
            targetMasked: backupMasked,
            session: newSession
          }
        })
        return
      }
      addAccountToken({ accountId, dashxToken, token: accessToken })
      dx.setIdentity(accountId, dashxToken)
      login({ accountId })
    },
    onError: () => {
      showAlertFailure({
        message: t('mfa.verify.invalidCode')
      })
      setCode('')
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  })

  const [sendMfaCode, { loading: isResending }] = useMutation(
    SEND_MFA_CODE_MUTATION,
    {
      onCompleted: () =>
        showAlertSuccess({
          message: `Your code has been resent.
        Please check your ${
  backupChannel === 'email' || channel === 'email'
    ? backupChannel || channel
    : 'phone'
}.`
        })
    }
  )

  if (!account || !session) {
    history.replace('/login')
    return null
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (!code || code.length !== 6) return

    if (isAuthenticator) {
      completeMfaEnrollment({
        code,
        accessToken: session?.accessToken
      })
    } else {
      verifyMfaCode({
        code,
        accessToken: session?.accessToken
      })
    }
  }

  const handleResend = () => {
    if (isAuthenticator) return

    sendMfaCode({
      channel,
      target: channel === 'sms' ? null : account.email,
      accessToken: session?.accessToken
    })
  }

  const handleUseBackup = () => {
    if (!backupChannel) {
      history.push({
        pathname: '/mfa/backup',
        state: { account, session, mfaContext, from: 'verify' }
      })
      return
    }

    sendMfaCode({
      channel: backupChannel,
      target: backupChannel === 'sms' ? null : account.email,
      accessToken: session?.accessToken
    }).then(res => {
      const { targetMasked } = res.data.sendMfaCode

      history.push({
        pathname: '/mfa/verify',
        state: {
          account,
          session,
          mfaContext: {
            ...mfaContext,
            backup_target_masked: targetMasked
          },
          channel: backupChannel
        }
      })
    })
  }

  return (
    <Fragment>
      <AuthTitle title={title} subtitle={subtitle} subtitlePosition="below" />
      <Row>
        <Column span={3} hiddenMd hiddenXs />
        <Column span={6} spanMd={12}>
          <Card variant="secondary">
            <form onSubmit={handleSubmit}>
              <TextInput
                ref={inputRef}
                placeholder="_ _ _ _ _ _"
                input={{
                  value: code,
                  onChange: e =>
                    setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))
                }}
                autoFocus
              />
              <AuthFormFooter>
                <Button
                  type="submit"
                  label={t('mfa.buttons.confirm')}
                  disabled={!code || code.length !== 6}
                />
              </AuthFormFooter>
            </form>
          </Card>
          <Stack justify="center">
            <Stack gutter={2} className={classes.resend}>
              <Text size="xSmall" color="light" weight="medium">
                {isAuthenticator
                  ? t('mfa.verify.cantAccessApp')
                  : t('mfa.verify.didntGetCode')}
              </Text>
              <Text
                color="tertiary"
                size="xSmall"
                weight="medium"
                disabled={isResending}
                onClick={isAuthenticator ? handleUseBackup : handleResend}
                decoration="underline"
                className={classes.pointer}
              >
                {isAuthenticator
                  ? t('mfa.verify.useBackupLink')
                  : t('mfa.verify.resend')}
              </Text>
            </Stack>
          </Stack>
        </Column>
      </Row>
    </Fragment>
  )
}

export default MfaVerifyPage
